import React, { useState, useEffect } from 'react';
import { maklumatPelayananService } from '../../../services/maklumatPelayananService';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaSearch, FaFilePdf, FaDownload
} from 'react-icons/fa';

const ManajemenMaklumatPelayanan = () => {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [fileUpload, setFileUpload] = useState(null);

    const [formData, setFormData] = useState({
        judul: '',
        deskripsi: '',
        order: 0,
        status: 'publish',
    });

    const loadData = async () => {
        setLoading(true);
        const result = await maklumatPelayananService.getMaklumatList({ search: searchTerm });
        if (result.success) {
            setDataList(result.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            judul: '',
            deskripsi: '',
            order: dataList.length + 1,
            status: 'publish',
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            judul: item.judul || '',
            deskripsi: item.deskripsi || '',
            order: item.order || 0,
            status: item.status || 'publish',
        });
        setFileUpload(null);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        setFileUpload(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!editing && !fileUpload) {
            showNotif('File PDF wajib diupload', 'error');
            return;
        }

        setSubmitting(true);

        const submitData = new FormData();
        submitData.append('judul', formData.judul);
        submitData.append('deskripsi', formData.deskripsi || '');
        submitData.append('order', formData.order);
        submitData.append('status', formData.status);

        if (fileUpload) {
            submitData.append('file', fileUpload);
        }

        let result;
        if (editing) {
            result = await maklumatPelayananService.updateMaklumat(editing.id, submitData);
        } else {
            result = await maklumatPelayananService.createMaklumat(submitData);
        }

        if (result.success) {
            showNotif(result.data?.message || 'Data berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await maklumatPelayananService.deleteMaklumat(deleteConfirm.id);
        if (result.success) {
            showNotif('Data berhasil dihapus');
            loadData();
        } else {
            showNotif(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'publish' ? 'draft' : 'publish';
        const result = await maklumatPelayananService.updateStatus(item.id, newStatus);
        if (result.success) {
            showNotif('Status berhasil diubah');
            loadData();
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const filteredData = dataList.filter(item =>
        item.judul?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Maklumat Pelayanan</h2>
                    <p className="text-gray-600">Kelola dokumen maklumat pelayanan (PDF)</p>
                </div>
                <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                    <FaPlus className="mr-2" /> Tambah Maklumat
                </button>
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                        ? 'bg-green-50 border border-green-200 text-green-700'
                        : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success'
                        ? <FaCheckCircle className="mr-2" />
                        : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {/* Search */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari maklumat pelayanan..."
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">No</th>
                                <th className="p-4">Judul</th>
                                <th className="p-4">File</th>
                                <th className="p-4">Ukuran</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Diperbarui</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-8 text-center text-gray-500">
                                        Belum ada data maklumat pelayanan
                                    </td>
                                </tr>
                            ) : (
                                filteredData.map((item, index) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4 text-gray-500">{index + 1}</td>
                                        <td className="p-4">
                                            <p className="font-semibold text-[#0F4C3A]">{item.judul}</p>
                                            {item.deskripsi && (
                                                <p className="text-xs text-gray-400 line-clamp-1">{item.deskripsi}</p>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <a
                                                href={getFileUrl(item.file_path)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-red-500 hover:text-red-700 text-sm"
                                            >
                                                <FaFilePdf />
                                                {item.file_name || 'Lihat PDF'}
                                            </a>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">
                                            {formatFileSize(item.file_size)}
                                        </td>
                                        <td className="p-4">
                                            <button
                                                onClick={() => handleToggleStatus(item)}
                                                className={`px-3 py-1 rounded-full text-xs font-medium ${item.status === 'publish'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-yellow-100 text-yellow-700'
                                                    }`}
                                            >
                                                {item.status === 'publish' ? 'Publish' : 'Draft'}
                                            </button>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500">
                                            {formatDate(item.updated_at)}
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => handleEdit(item)}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg"
                                                    title="Edit"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirm(item)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                                    title="Hapus"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-[#0F4C3A]">
                                    {editing ? 'Edit Maklumat Pelayanan' : 'Tambah Maklumat Pelayanan'}
                                </h3>
                                <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                    <FaTimes size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Judul <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="judul"
                                        value={formData.judul}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Contoh: Maklumat Pelayanan BPKAD Kota Magelang"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Deskripsi
                                    </label>
                                    <textarea
                                        name="deskripsi"
                                        value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="Deskripsi singkat (opsional)"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Urutan
                                        </label>
                                        <input
                                            type="number"
                                            name="order"
                                            value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        >
                                            <option value="publish">Publish (Tampil)</option>
                                            <option value="draft">Draft</option>
                                            <option value="arsip">Arsip</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Upload File PDF {!editing && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        onChange={handleFileChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: PDF. Maks: 10MB
                                    </p>

                                    {editing && editing.file_name && !fileUpload && (
                                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                                            <FaFilePdf className="text-red-500" />
                                            File saat ini: {editing.file_name}
                                        </div>
                                    )}

                                    {fileUpload && (
                                        <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                                            <FaCheckCircle />
                                            File baru: {fileUpload.name} ({(fileUpload.size / 1024).toFixed(1)} KB)
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                                Mengupload...
                                            </>
                                        ) : (
                                            <>
                                                <FaSave className="mr-2" />
                                                {editing ? 'Update' : 'Simpan'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Data?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.judul}</strong>.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-6 py-2.5 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Batal
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenMaklumatPelayanan;